import React, { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { tokenStorage } from '../utils/secureStorage';
import { secureLogger } from '../utils/secureLogger';
import { sessionDebug } from '../utils/sessionDebug';

const SessionManager = () => {
  const { logout, isAuthenticated } = useAuth();
  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (!isAuthenticated) return;
    
    const now = Date.now();
    lastActivityRef.current = now;
    
    // Store last activity time in localStorage for persistence
    localStorage.setItem('lastUserActivity', now.toString());
    
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    inactivityTimerRef.current = setTimeout(() => {
      secureLogger.info('User inactive for 10 minutes, logging out');
      if (process.env.NODE_ENV === 'development') {
        console.log('Session Debug - Inactivity timeout triggered');
        sessionDebug.logSessionStatus();
      }
      logout(true); // Silent logout for inactivity
    }, INACTIVITY_TIMEOUT);
  }, [isAuthenticated, logout]);

  // Handle user activity events
  const handleUserActivity = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // Handle page visibility change
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      // Page is hidden (tab closed, minimized, etc.)
      // Don't logout immediately, just mark the time
      localStorage.setItem('pageHiddenTime', Date.now().toString());
    } else {
      // Page is visible again - check if user was inactive too long
      const pageHiddenTime = localStorage.getItem('pageHiddenTime');
      const lastActivity = localStorage.getItem('lastUserActivity');
      
      if (pageHiddenTime && lastActivity) {
        const hiddenTime = parseInt(pageHiddenTime);
        const lastActivityTime = parseInt(lastActivity);
        const inactiveTime = Date.now() - Math.max(hiddenTime, lastActivityTime);
        
        if (inactiveTime > INACTIVITY_TIMEOUT) {
          secureLogger.info('User was inactive too long while away, logging out');
          logout(true); // Silent logout for inactivity
          return;
        }
      }
      
      // Clear the hidden time marker
      localStorage.removeItem('pageHiddenTime');
      resetInactivityTimer();
    }
  }, [logout, resetInactivityTimer]);

  // Handle beforeunload event (tab/window closing)
  const handleBeforeUnload = useCallback(() => {
    // Don't remove token on page refresh - let the AuthContext handle session restoration
    // Only mark the time for potential inactivity check
    localStorage.setItem('pageUnloadTime', Date.now().toString());
    secureLogger.info('Page unloading - preserving session for potential restoration');
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timer if user is not authenticated
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return;
    }

    // Check if we're returning from a page refresh
    const pageUnloadTime = localStorage.getItem('pageUnloadTime');
    if (pageUnloadTime) {
      const unloadTime = parseInt(pageUnloadTime);
      const timeSinceUnload = Date.now() - unloadTime;
      
      // If less than 5 seconds have passed, it was likely a refresh
      if (timeSinceUnload < 5000) {
        secureLogger.info('Detected page refresh - restoring session');
        if (process.env.NODE_ENV === 'development') {
          console.log('Session Debug - Page refresh detected, restoring session');
        }
        // Reset activity timer immediately
        resetInactivityTimer();
      }
      
      // Clear the unload time marker
      localStorage.removeItem('pageUnloadTime');
    }

    // Set up activity listeners
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'focus'
    ];

    activityEvents.forEach(event => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    // Set up visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Set up beforeunload listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Start the inactivity timer
    resetInactivityTimer();

    if (process.env.NODE_ENV === 'development') {
      console.log('Session Debug - SessionManager initialized for authenticated user');
    }

    // Periodic session validation check (every 5 minutes)
    const sessionCheckInterval = setInterval(() => {
      if (!tokenStorage.hasValidToken()) {
        secureLogger.info('Token expired during active session, logging out');
        if (process.env.NODE_ENV === 'development') {
          console.log('Session Debug - Token expired, logging out');
        }
        logout(true); // Silent logout for token expiration
      }
    }, 5 * 60 * 1000); // 5 minutes

    // Cleanup function
    return () => {
      clearInterval(sessionCheckInterval);
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [isAuthenticated, handleUserActivity, handleVisibilityChange, handleBeforeUnload, resetInactivityTimer]);

  // This component doesn't render anything
  return null;
};

export default SessionManager; 